import React, { Fragment } from "react";
import { Badge, Button, Card, CardBody, CardTitle, Row } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";
import { AddCommaNumber } from "../../../helpers/Utils";

const calculateVariantPriceRange = (variants) => {
  if (!variants || variants.length === 0) {
    return null;
  }

  let minPrice = Infinity;
  let maxPrice = -Infinity;
  let minPromoPrice = Infinity;
  let maxPromoPrice = -Infinity;

  let hasActivePromotion = false;

  // Lấy ngày hiện tại và set giờ về 0 để so sánh ngày chính xác
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  variants.forEach((variant) => {
    const price = parseFloat(variant.price);
    const promoPrice = parseFloat(variant.promo_price);

    // Cập nhật dải giá gốc
    if (!isNaN(price)) {
      if (price < minPrice) minPrice = price;
      if (price > maxPrice) maxPrice = price;
    }

    let isPromoActive = false;

    if (!isNaN(promoPrice) && promoPrice > 0) {
      const startDate = variant.date_start
        ? new Date(variant.date_start)
        : null;
      const endDate = variant.date_end ? new Date(variant.date_end) : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(0, 0, 0, 0);

      // Case 1: Có cả ngày bắt đầu và kết thúc
      if (startDate && endDate) {
        if (today >= startDate && today <= endDate) {
          isPromoActive = true;
        }
      }
      // Case 2: Chỉ có ngày bắt đầu, không có ngày kết thúc
      else if (startDate && !endDate) {
        if (today.getTime() >= startDate.getTime()) {
          isPromoActive = true;
        }
      }
      // Case 3: Không có ngày bắt đầu/kết thúc nhưng có giá KM
      else if (!startDate && !endDate) {
        isPromoActive = true;
      }
    }

    if (isPromoActive) {
      hasActivePromotion = true;
      if (promoPrice < minPromoPrice) minPromoPrice = promoPrice;
      if (promoPrice > maxPromoPrice) maxPromoPrice = promoPrice;
    }
  });

  // Nếu không tìm thấy khuyến mãi nào đang hoạt động, trả về null cho dải giá KM
  if (!hasActivePromotion) {
    minPromoPrice = null;
    maxPromoPrice = null;
  }

  return {
    minPrice: minPrice === Infinity ? null : minPrice,
    maxPrice: maxPrice === -Infinity ? null : maxPrice,
    minPromoPrice,
    maxPromoPrice,
  };
};

const ContentDisplay = (props) => {
  let priceDisplayContent = null;
  const hasVariants = props.values.variant && props.values.variant.length > 0;

  if (hasVariants) {
    const range = calculateVariantPriceRange(props.values.variant);
    if (range) {
      // Hiển thị dải giá cho sản phẩm có biến thể
      const priceRangeText =
        range.minPrice === range.maxPrice
          ? AddCommaNumber(range.minPrice)
          : `${AddCommaNumber(range.minPrice)} - ${AddCommaNumber(range.maxPrice)}`;

      const promoRangeText =
        range.minPromoPrice && range.maxPromoPrice
          ? range.minPromoPrice === range.maxPromoPrice
            ? AddCommaNumber(range.minPromoPrice)
            : `${AddCommaNumber(range.minPromoPrice)} - ${AddCommaNumber(range.maxPromoPrice)}`
          : null;

      priceDisplayContent = (
        <>
          {promoRangeText ? (
            <>
              <Badge color="light" pill className="mr-1 mb-1">
                <del>{priceRangeText}</del>
              </Badge>
              <Badge color="primary" pill>
                {promoRangeText}
              </Badge>
            </>
          ) : (
            <Badge color="primary" pill>
              {priceRangeText}
            </Badge>
          )}
        </>
      );
    }
  } else {
    // Logic hiển thị giá gốc cho sản phẩm không có biến thể
    priceDisplayContent = (
      <>
        {props.values.active_sale && parseFloat(props.values.price_sale) > 0 ? (
          <>
            <Badge color="light" pill className="mr-1 mb-1">
              <del>{AddCommaNumber(parseFloat(props.values.price))}</del>
            </Badge>
            <Badge color="primary" pill>
              {AddCommaNumber(parseFloat(props.values.price_sale))}
            </Badge>
          </>
        ) : (
          <Badge color="primary" pill>
            {AddCommaNumber(parseFloat(props.values.price))}
          </Badge>
        )}
      </>
    );
  }

  // console.log('render ContentDisplay');
  return (
    <Card className="mb-4">
      <CardBody>
        <CardTitle>
          <IntlMessages id="product.title" />
        </CardTitle>
        <Row className="mt-3 ">
          <Colxx xxs="12" lg="6">
            <p className="text-muted text-small mb-2">
              <IntlMessages id="name" />
            </p>
            <p className="mb-3 text-capitalize">
              {props.values.name ? (
                props.values.name
              ) : (
                <span className="text-lowercase">
                  (<IntlMessages id="home.summary.no-info" />)
                </span>
              )}
            </p>
          </Colxx>
          <Colxx xxs="12" lg="6">
            <p className="text-muted text-small mb-2">
              <IntlMessages id="category.thumb-list" />
            </p>
            <p className="mb-3">{props.values.category?.name}</p>
          </Colxx>
        </Row>
        <Row>
          <Colxx xxs="12" lg="6">
            <p className="text-muted text-small mb-2">
              <IntlMessages id="pages.product-sku" />
            </p>
            <p className="mb-3">{props.sku}</p>
          </Colxx>
          <Colxx xxs="12" lg="6">
            <p className="text-muted text-small mb-2">
              <IntlMessages id="pages.product-barcode" />
            </p>
            <p className="mb-3">
              {props.values.barcode ? (
                props.values.barcode
              ) : (
                <span className="text-lowercase">
                  (<IntlMessages id="home.summary.no-setup" />)
                </span>
              )}
            </p>
          </Colxx>
        </Row>
        <Row>
          <Colxx xxs="12">
            <p className="text-muted text-small mb-2">
              <IntlMessages id="product.price.original" />
            </p>
            <p className="mb-3 text-lowercase">
              {priceDisplayContent}
              {/* {props.values.price_sale !== '0' ? (
                                <>
                                    <Badge
                                        color='primary'
                                        pill
                                        className='spacer-around-1'
                                    >
                                        {AddCommaNumber(
                                            props.values.price_sale
                                        )}
                                    </Badge>
                                    <Badge
                                        color='light'
                                        pill
                                        className='spacer-around-1'
                                    >
                                        <del>
                                            {AddCommaNumber(props.values.price)}
                                        </del>
                                    </Badge>
                                </>
                            ) : (
                                <Badge color='primary' pill>
                                    {AddCommaNumber(props.values.price)}
                                </Badge>
                            )} */}
            </p>
          </Colxx>
        </Row>
        {/* {props.values?.variant && <p className='text-muted text-small'>Các biến thể của sản phẩm</p>}
                {props.values?.variant &&  [...props.values?.variant].sort((a, b) => a.id - b.id).map(v => {
                    return (
                        <Card className='mb-3'>
                            <CardBody className='p-3'>
                                <Row className='px-3 py-2'>{v.sku}</Row>
                                <Row className='justify-content-between py-3'>
                                    <Colxx xxs='4'>
                                        <p className='text-muted text-small mb-2'>Size</p>
                                        {v?.attributes?.size?.label ? <Badge color='primary' pill>{v?.attributes?.size?.label}</Badge> : 'Chưa thiết lập'}
                                    </Colxx>
                                    <Colxx xxs='4'>
                                        <p className='text-muted text-small mb-2'>Màu sắc</p>
                                        {v?.attributes?.color?.label ? <Badge color='primary' pill>{v?.attributes?.color?.label}</Badge> : 'Chưa thiết lập'}
                                    </Colxx>
                                    <Colxx xxs='4'>
                                        <p className='text-muted text-small mb-2'>Chất liệu</p>
                                        {v?.attributes?.material?.label ? <Badge color='primary' pill>{v?.attributes?.color?.label}</Badge> : 'Chưa thiết lập'}
                                    </Colxx>
                                </Row>
                                <Row className='justify-content-between py-3'>
                                    <Colxx xxs='6'>
                                        <p className='text-muted text-small mb-2'>Giá bán</p>
                                        <Badge color='primary' pill>{AddCommaNumber(v?.price) || props.values.price}</Badge>
                                    </Colxx>
                                    {v?.promo_price ? 
                                        <Colxx xxs='6'>
                                            <p className='text-muted text-small mb-2'>Giá khuyến mãi</p>
                                            <Badge color='primary' pill>{AddCommaNumber(v?.promo_price) || props.values.price_sale}</Badge>
                                        </Colxx>
                                    : null}
                                </Row>
                                <Row className='justify-content-between py-2'>
                                    {v?.date_start ? 
                                        <Colxx xxs='6'>
                                            <p className='text-muted text-small mb-2'>Ngày bắt đầu khuyến mãi</p>
                                            {v?.date_start ? <Badge color='primary' pill>{v?.date_start}</Badge> : 'Chưa thiết lập'}
                                        </Colxx>
                                    : null}
                                    {v?.date_end ? 
                                        <Colxx xxs='6'>
                                            <p className='text-muted text-small mb-2'>Ngày kết thúc khuyến mãi</p>
                                            {v?.date_end ? <Badge color='primary' pill>{v?.date_end}</Badge> : 'Chưa thiết lập'}
                                        </Colxx>
                                    : null }
                                </Row>
                            </CardBody>
                        </Card>
                    )
                })
                } */}
        {/* {props.values?.size?.length ? <>
                    <Row>
                        <Colxx xxs='6' lg='6'>
                            <p className='text-muted text-small mb-2'>
                                {props.values.sizeTitle ? (
                                    <span>{props.values.sizeTitle}</span>
                                ) : (
                                    <IntlMessages id='product.size' />
                                )}
                            </p>
                        </Colxx>
                        <Colxx xxs='6' lg='6'>
                            <p className='text-muted text-small mb-2'>
                                <IntlMessages id='product.add_price' />
                            </p>
                        </Colxx>
                    </Row>
                    <Row className='mb-4'>
                        {props.values?.size?.length ? (
                            props.values?.size?.map((s, idx) => (
                                <Fragment key={idx}>
                                    <Colxx xxs='6' lg='6'>
                                        <p className='text-capitalize'>{s.name}</p>
                                    </Colxx>
                                    <Colxx xxs='6' lg='6'>
                                        <Badge
                                            color='primary'
                                            pill
                                            className='spacer-around-1'
                                        >
                                            {AddCommaNumber(s.value)}
                                        </Badge>
                                    </Colxx>
                                </Fragment>
                            ))
                        ) : (
                            <span className='p-rl-15px'>(<IntlMessages id='home.summary.no-setup' />)</span>
                        )}
                    </Row> 
                </> : null} */}
        {props.values?.topping?.length ? (
          <>
            <Row>
              <Colxx xxs="6" lg="6">
                <p className="text-muted text-small mb-2">
                  {props.values.toppingTitle ? (
                    <span>{props.values.toppingTitle}</span>
                  ) : (
                    <IntlMessages id="product.topping" />
                  )}
                </p>
              </Colxx>
              <Colxx xxs="6" lg="6">
                <p className="text-muted text-small mb-2">
                  <IntlMessages id="product.add_price" />
                </p>
              </Colxx>
            </Row>
            <Row className="mb-4">
              {props.values?.topping?.length ? (
                props.values?.topping?.map((s, idx) => (
                  <Fragment key={idx}>
                    <Colxx xxs="6" lg="6">
                      <p className="text-capitalize">{s.name}</p>
                    </Colxx>
                    <Colxx xxs="6" lg="6">
                      <Badge color="primary" pill className="spacer-around-1">
                        {AddCommaNumber(s.value)}
                      </Badge>
                    </Colxx>
                  </Fragment>
                ))
              ) : (
                <span className="p-rl-15px">
                  (<IntlMessages id="home.summary.no-setup" />)
                </span>
              )}
            </Row>
          </>
        ) : null}
        {props.values?.sugar?.length ? (
          <>
            <Row>
              <Colxx xxs="12" lg="12">
                <p className="text-muted text-small mb-2">
                  {props.values.sugarTitle ? (
                    <span>{props.values.sugarTitle}</span>
                  ) : (
                    <IntlMessages id="product.sugar" />
                  )}
                </p>
              </Colxx>
            </Row>
            <div className="d-flex mb-4">
              {props.values?.sugar?.length ? (
                props.values?.sugar?.map((_item, idx) => (
                  <Fragment key={idx}>
                    <Colxx _id={idx} className="pad-2px w-20">
                      <Button color="secondary" size="sm" outline block>
                        {`${_item} %`}
                      </Button>
                    </Colxx>
                  </Fragment>
                ))
              ) : (
                <span className="p-rl-15px">
                  (<IntlMessages id="home.summary.no-setup" />)
                </span>
              )}
            </div>
          </>
        ) : null}
        {props.values?.ice?.length ? (
          <>
            <Row>
              <Colxx xxs="12" lg="6">
                <p className="text-muted text-small mb-2">
                  {props.values.iceTitle ? (
                    <span>{props.values.iceTitle}</span>
                  ) : (
                    <IntlMessages id="product.ice" />
                  )}
                </p>
              </Colxx>
            </Row>

            <div className="d-flex">
              {props.values?.ice?.length ? (
                props.values?.ice?.map((_item, idx) => (
                  <Fragment key={idx}>
                    <Colxx _id={idx} className="pad-2px w-20">
                      <Button color="secondary" size="sm" outline block>
                        {`${_item} %`}
                      </Button>
                    </Colxx>
                  </Fragment>
                ))
              ) : (
                <span className="p-rl-15px">
                  (<IntlMessages id="home.summary.no-setup" />)
                </span>
              )}
            </div>
          </>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default injectIntl(ContentDisplay);
